import type {
	ButtonHTMLAttributes,
	HTMLAttributes,
	InputHTMLAttributes,
	LabelHTMLAttributes,
	ReactNode
} from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { cn } from '$lib/utils';

type Variant = 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

const buttonVariants: Record<Variant, string> = {
	default: 'bg-primary text-primary-foreground shadow-card hover:bg-primary/90',
	secondary: 'bg-secondary text-secondary-foreground shadow-card hover:bg-secondary/80',
	outline: 'border border-border bg-background hover:bg-muted',
	ghost: 'hover:bg-muted',
	destructive: 'bg-destructive text-white shadow-card hover:bg-destructive/90'
};

const buttonSizes: Record<Size, string> = {
	sm: 'h-8 px-3 text-xs',
	md: 'h-10 px-4 text-sm',
	lg: 'h-11 px-5 text-sm'
};

export function Button({
	className,
	variant = 'default',
	size = 'md',
	...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
	return (
		<button
			className={cn(
				'inline-flex items-center justify-center gap-2 rounded-btn font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50',
				buttonVariants[variant],
				buttonSizes[size],
				className
			)}
			{...props}
		/>
	);
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(
				'min-w-0 rounded-card border border-border bg-card text-card-foreground shadow-card',
				className
			)}
			{...props}
		/>
	);
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('flex flex-col gap-1.5 p-5', className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
	return <h2 className={cn('text-sm font-semibold tracking-normal', className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
	return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('p-5 pt-0', className)} {...props} />;
}

export function Badge({
	className,
	variant = 'secondary',
	...props
}: HTMLAttributes<HTMLSpanElement> & { variant?: Variant | 'success' | 'warning' }) {
	const variants: Record<string, string> = {
		default: 'bg-primary/10 text-primary',
		secondary: 'bg-secondary text-secondary-foreground',
		outline: 'border border-border text-foreground',
		ghost: 'bg-muted text-muted-foreground',
		destructive: 'bg-destructive/10 text-destructive',
		success: 'bg-success/10 text-success',
		warning: 'bg-warning/15 text-warning-foreground'
	};
	return (
		<span
			className={cn(
				'inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold',
				variants[variant],
				className
			)}
			{...props}
		/>
	);
}

// ── Task 2: StatusChip ──────────────────────────────────────────────────────
const statusChip = tv({
	base: 'inline-flex items-center justify-center rounded-pill px-2.5 h-7 text-[11px] font-bold whitespace-nowrap',
	variants: {
		tone: {
			amber: 'bg-chip-amber-bg text-chip-amber-fg',
			purple: 'bg-chip-purple-bg text-chip-purple-fg',
			blue: 'bg-chip-blue-bg text-chip-blue-fg',
			green: 'bg-chip-green-bg text-chip-green-fg',
			red: 'bg-chip-red-bg text-chip-red-fg',
			neutral: 'bg-chip-neutral-bg text-chip-neutral-fg'
		}
	},
	defaultVariants: { tone: 'neutral' }
});

export function StatusChip({
	tone,
	className,
	children
}: {
	tone?: 'amber' | 'purple' | 'blue' | 'green' | 'red' | 'neutral';
	className?: string;
	children: ReactNode;
}) {
	return <span className={statusChip({ tone, className })}>{children}</span>;
}

// ── Task 3: MetricCard ──────────────────────────────────────────────────────
const metricValueTone = {
	primary: 'text-primary',
	warning: 'text-warning',
	danger: 'text-destructive',
	default: 'text-foreground'
} as const;

export function MetricCard({
	label,
	value,
	tone = 'default',
	className
}: {
	label: string;
	value: ReactNode;
	tone?: keyof typeof metricValueTone;
	className?: string;
}) {
	return (
		<div
			className={cn(
				'rounded-card border border-border bg-card px-3.5 py-3 shadow-card',
				className
			)}
		>
			<p className="text-[11px] font-medium text-muted-foreground">{label}</p>
			<p className={cn('mt-1 text-xl font-bold tabular-nums', metricValueTone[tone])}>{value}</p>
		</div>
	);
}

// ── Task 4: ListRow ─────────────────────────────────────────────────────────
const dotTone = {
	primary: 'bg-primary',
	warning: 'bg-warning',
	danger: 'bg-destructive',
	green: 'bg-chip-green-fg',
	neutral: 'bg-muted-foreground'
} as const;

export function ListRow({
	title,
	subtitle,
	trailing,
	iconTone = 'primary',
	onClick,
	href,
	className
}: {
	title: ReactNode;
	subtitle?: ReactNode;
	trailing?: ReactNode;
	iconTone?: keyof typeof dotTone;
	onClick?: () => void;
	href?: string;
	className?: string;
}) {
	const inner = (
		<>
			<span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-muted">
				<span className={cn('size-2.5 rounded-full', dotTone[iconTone])} />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-bold text-foreground">{title}</span>
				{subtitle ? (
					<span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{subtitle}</span>
				) : null}
			</span>
			{trailing ? <span className="shrink-0">{trailing}</span> : null}
		</>
	);
	const base =
		'flex min-h-14 w-full items-center gap-3 rounded-card border border-border bg-card px-4 py-3 text-left shadow-card';
	if (href)
		return (
			<a href={href} className={cn(base, 'transition-colors hover:bg-muted/40', className)}>
				{inner}
			</a>
		);
	if (onClick)
		return (
			<button
				type="button"
				onClick={onClick}
				className={cn(base, 'transition-colors hover:bg-muted/40', className)}
			>
				{inner}
			</button>
		);
	return <div className={cn(base, className)}>{inner}</div>;
}

// ── Task 5: SectionHeader + MobileHeader ────────────────────────────────────
export function SectionHeader({
	children,
	action,
	className
}: {
	children: ReactNode;
	action?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn('flex items-center justify-between', className)}>
			<h2 className="text-[15px] font-bold text-foreground">{children}</h2>
			{action ?? null}
		</div>
	);
}

export function MobileHeader({
	title,
	subtitle,
	trailing,
	className
}: {
	title: ReactNode;
	subtitle?: ReactNode;
	trailing?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn('flex items-start justify-between gap-3', className)}>
			<div className="min-w-0">
				<h1 className="truncate text-[22px] font-bold leading-8 text-foreground">{title}</h1>
				{subtitle ? (
					<p className="mt-1 truncate text-xs font-medium text-muted-foreground">{subtitle}</p>
				) : null}
			</div>
			{trailing ? <div className="shrink-0">{trailing}</div> : null}
		</div>
	);
}

// ── Task 6: CommandCard ─────────────────────────────────────────────────────
export function CommandCard({
	chip,
	title,
	subtitle,
	action,
	aside,
	className
}: {
	chip?: ReactNode;
	title: ReactNode;
	subtitle?: ReactNode;
	action?: ReactNode;
	aside?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn('relative overflow-hidden rounded-hero bg-surface-dark p-6 shadow-hero', className)}>
			{aside ? <div className="absolute right-5 top-5">{aside}</div> : null}
			{chip ? <div className="mb-4">{chip}</div> : null}
			<h2 className="text-[25px] font-bold leading-8 text-white">{title}</h2>
			{subtitle ? (
				<p className="mt-2 text-xs font-medium text-surface-dark-foreground">{subtitle}</p>
			) : null}
			{action ? <div className="mt-4 flex justify-end">{action}</div> : null}
		</div>
	);
}

// ── Task 8: BottomSheet ─────────────────────────────────────────────────────
export function BottomSheet({
	open,
	onClose,
	title,
	children
}: {
	open: boolean;
	onClose: () => void;
	title?: ReactNode;
	children: ReactNode;
}) {
	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		document.addEventListener('keydown', onKey);
		return () => {
			document.body.style.overflow = prev;
			document.removeEventListener('keydown', onKey);
		};
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
			<button
				type="button"
				aria-label="닫기"
				className="absolute inset-0 bg-foreground/40"
				onClick={onClose}
			/>
			<div className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-[1.5rem] border-t border-border bg-card pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-[0_-8px_30px_rgb(15_23_42/0.18)]">
				{title ? (
					<div className="flex items-center justify-between border-b border-border px-4 py-3">
						<p className="text-sm font-semibold">{title}</p>
						<button
							type="button"
							aria-label="시트 닫기"
							onClick={onClose}
							className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"
						>
							<X className="size-5" />
						</button>
					</div>
				) : null}
				<div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
			</div>
		</div>
	);
}

// ── Task 9: FilterChips ─────────────────────────────────────────────────────
export function FilterChips({
	options,
	value,
	onChange,
	className
}: {
	options: { value: string; label: string }[];
	value: string;
	onChange: (value: string) => void;
	className?: string;
}) {
	return (
		<div className={cn('flex gap-2 overflow-x-auto', className)} role="tablist">
			{options.map((opt) => {
				const active = opt.value === value;
				return (
					<button
						key={opt.value}
						type="button"
						role="tab"
						aria-selected={active}
						onClick={() => onChange(opt.value)}
						className={cn(
							'h-7 shrink-0 rounded-pill px-3.5 text-[11px] font-bold transition-colors',
							active
								? 'bg-chip-blue-bg text-chip-blue-fg'
								: 'border border-border bg-card text-muted-foreground'
						)}
					>
						{opt.label}
					</button>
				);
			})}
		</div>
	);
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
	return (
		<input
			className={cn(
				'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring/35 flex h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
				className
			)}
			{...props}
		/>
	);
}

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
	return (
		<label className={cn('text-xs font-medium text-muted-foreground', className)} {...props} />
	);
}

export function Spinner({ className, size = 'md' }: { className?: string; size?: Size }) {
	const sizes: Record<Size, string> = { sm: 'size-4', md: 'size-5', lg: 'size-7' };
	return (
		<span
			className={cn(
				'inline-block animate-spin rounded-full border-2 border-current border-r-transparent',
				sizes[size],
				className
			)}
			aria-label="불러오는 중"
		/>
	);
}

export function EmptyState({ message }: { message: string }) {
	return (
		<div className="flex min-h-32 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 p-6 text-sm text-muted-foreground">
			{message}
		</div>
	);
}

export function PageHeader({
	title,
	description,
	actions
}: {
	title: string;
	description?: string;
	actions?: ReactNode;
}) {
	return (
		<div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
			<div className="min-w-0 space-y-1">
				<h1 className="text-xl font-semibold tracking-normal">{title}</h1>
				{description ? (
					<p className="text-wrap text-sm text-muted-foreground">{description}</p>
				) : null}
			</div>
			{actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
		</div>
	);
}

type ConfirmOptions = {
	title: string;
	description?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	destructive?: boolean;
};

type ConfirmState = ConfirmOptions & {
	resolve: (value: boolean) => void;
};

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<ConfirmState | null>(null);

	const askConfirm = useCallback((options: ConfirmOptions) => {
		return new Promise<boolean>((resolve) => setState({ ...options, resolve }));
	}, []);

	const close = useCallback(
		(value: boolean) => {
			state?.resolve(value);
			setState(null);
		},
		[state]
	);

	const contextValue = useMemo(() => askConfirm, [askConfirm]);

	return (
		<ConfirmContext.Provider value={contextValue}>
			{children}
			{state ? (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
					<Card className="w-full max-w-md">
						<CardHeader className="flex-row items-start justify-between gap-4">
							<div className="space-y-1">
								<CardTitle>{state.title}</CardTitle>
								{state.description ? <CardDescription>{state.description}</CardDescription> : null}
							</div>
							<button
								type="button"
								className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
								onClick={() => close(false)}
								aria-label="닫기"
							>
								<X className="size-4" />
							</button>
						</CardHeader>
						<CardContent className="flex justify-end gap-2">
							<Button variant="outline" onClick={() => close(false)}>
								{state.cancelLabel ?? '취소'}
							</Button>
							<Button
								variant={state.destructive ? 'destructive' : 'default'}
								onClick={() => close(true)}
							>
								{state.confirmLabel ?? '확인'}
							</Button>
						</CardContent>
					</Card>
				</div>
			) : null}
		</ConfirmContext.Provider>
	);
}

export function useConfirm() {
	const context = useContext(ConfirmContext);
	if (!context) throw new Error('useConfirm must be used inside ConfirmProvider');
	return context;
}

// ── Task 10: screen-specific card components ────────────────────────────────
export * from '$lib/ui/screen-cards';
